/**
 * 工單預計 SN 標籤送印（be doc dd7f0319，WORK_ORDER_SN_LABEL_PRINTING_HANDOFF）。
 * - 印表機只選 profile key；IP／port／QR 網址都在後端設定。
 * - SENT 只代表指令已送出，不代表已出紙；不自動重送。
 */

export const SN_PRINTER_PROFILE_OPTIONS = [
  { value: 'work_order_sn_printer_1', label: '印表機 1' },
  { value: 'work_order_sn_printer_2', label: '印表機 2' },
];

export const SN_LABEL_TYPE_OPTIONS = [
  { value: 'T_LABEL', label: '雙欄小標籤' },
  { value: 'QR_LABEL', label: 'QR 標籤' },
];

export const SN_PRINT_RANGE_OPTIONS = [
  { value: 'ALL', label: '整段' },
  { value: 'PARTIAL', label: '連續子範圍' },
];

export const SN_LABEL_JOB_STATUS = {
  QUEUED: 'QUEUED',
  SENDING: 'SENDING',
  SENT: 'SENT',
  FAILED: 'FAILED',
  UNKNOWN: 'UNKNOWN',
  CANCELLED: 'CANCELLED',
};

const STATUS_LABELS = {
  QUEUED: '排隊中',
  SENDING: '送印中',
  SENT: '已送出',
  FAILED: '失敗',
  UNKNOWN: '結果不明',
  CANCELLED: '已取消',
};

export function formatSnLabelJobStatus(status) {
  return STATUS_LABELS[status] ?? status ?? '—';
}

export function formatSnPrinterProfile(profile) {
  return SN_PRINTER_PROFILE_OPTIONS.find((option) => option.value === profile)?.label
    ?? profile
    ?? '—';
}

export function formatSnLabelType(labelType) {
  return SN_LABEL_TYPE_OPTIONS.find((option) => option.value === labelType)?.label
    ?? labelType
    ?? '—';
}

export function isSnLabelJobCancellable(job) {
  return job?.status === SN_LABEL_JOB_STATUS.QUEUED;
}

/** 有排隊／送印中的 job 時需輪詢進度。 */
export function hasActiveSnLabelJob(jobs = []) {
  return (jobs ?? []).some((job) => (
    job?.status === SN_LABEL_JOB_STATUS.QUEUED || job?.status === SN_LABEL_JOB_STATUS.SENDING
  ));
}

/** 號段可送印：已配置且 RESERVED／CONSUMED（未核准、已釋放的後端會拒絕）。 */
export function canPrintSnLabels(serialAllocation) {
  return Boolean(serialAllocation)
    && (serialAllocation.status === 'RESERVED' || serialAllocation.status === 'CONSUMED');
}

/**
 * 子範圍檢查（後端仍會再驗）：整數、起 ≤ 迄、落在號段內。
 * @returns {string|null} 錯誤訊息
 */
export function validateSnPrintRange({ serialStart, serialEnd }, serialAllocation) {
  const start = Number(serialStart);
  const end = Number(serialEnd);
  if (!Number.isInteger(start) || !Number.isInteger(end) || serialStart === '' || serialEnd === '') {
    return '請填寫起訖流水號（整數）';
  }
  if (start > end) {
    return '起始流水號不可大於結束流水號';
  }
  const min = Number(serialAllocation?.serialStart);
  const max = Number(serialAllocation?.serialEnd);
  if (Number.isFinite(min) && Number.isFinite(max) && (start < min || end > max)) {
    return `流水號須在號段 ${min}–${max} 內`;
  }
  return null;
}

const ERROR_MESSAGES = {
  WORK_ORDER_SN_PRINT_NOT_APPROVED: '工單尚未核准，無法列印預計 SN',
  WORK_ORDER_SN_PRINT_ALLOCATION_MISSING: '工單沒有 SN 號段',
  WORK_ORDER_SN_PRINT_ALLOCATION_RELEASED: '工單 SN 號段已釋放',
  WORK_ORDER_SN_PRINT_RANGE_OUT_OF_ALLOCATION: '列印範圍超出工單號段',
  WORK_ORDER_SN_PRINT_RANGE_INVALID: '列印範圍不正確',
  WORK_ORDER_SN_PRINT_PRINTER_UNAVAILABLE: '印表機目前無法使用，請確認印表機狀態',
  WORK_ORDER_SN_PRINT_PRINTER_QUEUE_FULL: '此印表機排隊已滿（最多 5 筆），請稍後再送或改用另一台',
  WORK_ORDER_SN_PRINT_RANGE_OVERLAPS_PENDING_JOB: '與尚未完成的送印工作號段重疊，請調整範圍',
  WORK_ORDER_SN_PRINT_QR_URL_TEMPLATE_INVALID: '後端尚未設定 QR 網址，請聯絡系統管理者',
  WORK_ORDER_SN_PRINT_JOB_NOT_CANCELLABLE: '此工作已開始送印，無法取消',
  WORK_ORDER_SN_PRINT_JOB_NOT_FOUND: '找不到送印工作（可能已超過 24 小時被清除）',
};

export function resolveSnLabelJobErrorMessage(code) {
  return ERROR_MESSAGES[code] ?? null;
}
